import { useState } from "react";

import { generatePath, Link, useParams } from "react-router-dom";

import { getHiddenMentions, markRelevant } from "@/api/endpoints/searches";
import MentionRow from "@/views/HiddenMentionsView/partials/MentionRow";
import PaginationNew from "@/components/PaginationNew";
import AllEarsTitle from "@components/AllEarsTitle";

import { ROUTES } from "@/routes_new";
import { MentionV4 } from "@/types/MentionCard";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@components/ui/breadcrumb";
import Error from "@components/ui/error";
import { Separator } from "@components/ui/separator";
import { SidebarTrigger } from "@components/ui/sidebar";
import { Skeleton } from "@components/ui/skeleton";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { useLightSearch } from "@/hooks/useLightSearch";

const HiddenMentionsView = () => {
  const { t } = useLingui();

  // URL state
  const { orgUuid, uuid } = useParams<{
    orgUuid: string;
    uuid: string;
  }>();

  // Local state
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Server state
  const search = useLightSearch(uuid, orgUuid);

  const searchTitle = search?.title;

  const {
    data: mentions,
    error: mentionsError,
    refetch: refetchMentions,
    isLoading: isMentionsLoading,
  } = useQuery({
    queryKey: ["searches", uuid, "hidden", currentPage],
    queryFn: () => getHiddenMentions(uuid, { p: currentPage }),
    enabled: !!uuid,
  });

  const { mutateAsync: mutateMarkAsRelevant } = useMutation({
    mutationFn: (mentionIds: MentionV4["public_id"][]) =>
      markRelevant(mentionIds),
    onSuccess: (res) => {
      if (res.ok) {
        toast.success(t`Markerade omnämnadet som relevant`);
        refetchMentions();
      } else {
        toast.error(t`Lyckades inte markera omnämnandet som relevant`);
      }
    },
    onError: () => {
      toast.error(t`Lyckades inte markera omnämnandet som relevant`);
    },
  });

  // Handlers
  function handleMarkAsRelevant(mentionId: MentionV4["public_id"]) {
    return mutateMarkAsRelevant([mentionId]);
  }

  return (
    <>
      <AllEarsTitle title={L.MENTIONS_VIEW_HIDDEN_HEADER} />

      <header className="flex h-(--header-height) shrink-0 items-center gap-2 border-b transition-[width,height] ease-linear group-has-data-[collapsible=icon]/sidebar-wrapper:h-(--header-height)">
        <div className="flex w-full items-center gap-1 px-4 lg:gap-2 lg:px-6 max-w-7xl">
          <SidebarTrigger className="-ml-1" />

          <Separator
            orientation="vertical"
            className="mx-2 data-[orientation=vertical]:h-4"
          />

          <Breadcrumb>
            <BreadcrumbList>
              <BreadcrumbItem>
                <Trans>Bevakningar</Trans>
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                {searchTitle ? (
                  <BreadcrumbLink
                    render={
                      <Link
                        to={generatePath(ROUTES.MENTION_DETAIL_ORGID, {
                          uuid,
                          orgUuid,
                        })}
                      />
                    }
                  >
                    {searchTitle}
                  </BreadcrumbLink>
                ) : (
                  <Skeleton className="w-24 h-6" />
                )}
              </BreadcrumbItem>
              <BreadcrumbSeparator />
              <BreadcrumbItem>
                <BreadcrumbPage>
                  <Trans>Borttagna omnämnanden</Trans>
                </BreadcrumbPage>
              </BreadcrumbItem>
            </BreadcrumbList>
          </Breadcrumb>
        </div>
      </header>

      <div className="border-b">
        <div className="w-full p-4 lg:p-6 max-w-7xl flex justify-between items-end">
          <h1 className="text-2xl font-bold">
            <Trans>Borttagna omnämnanden</Trans>
          </h1>
        </div>
      </div>

      <div className="w-full p-4 lg:p-6 max-w-7xl flex flex-col gap-4">
        {mentions?.ok ? (
          <div>
            {mentions.data.mentions.length ? (
              <div className="flex flex-col gap-4">
                {mentions.data.mentions.map((mention) => (
                  <MentionRow
                    mention={mention}
                    key={mention.public_id}
                    onMarkRelevant={handleMarkAsRelevant}
                  />
                ))}

                <PaginationNew
                  page={currentPage}
                  max={mentions.data.meta.page_count}
                  onChangePage={setCurrentPage}
                />
              </div>
            ) : (
              <p data-testid="hidden-mentions-no-hits">
                <Trans>Inga omnämnanden hittades</Trans>
              </p>
            )}
          </div>
        ) : isMentionsLoading ? (
          <div className="flex flex-col gap-4">
            <Skeleton className="w-full h-64" />
            <Skeleton className="w-full h-64" />
            <Skeleton className="w-full h-64" />
          </div>
        ) : mentionsError ? (
          <Error
            heading={L.CUSTOMERS_SEARCH_500_ERROR_TITLE}
            description={L.CUSTOMERS_SEARCH_500_ERROR_MESSAGE}
            error={mentionsError}
          />
        ) : null}
      </div>
    </>
  );
};

export default HiddenMentionsView;
